import type {
  LanguageManualModule,
  LanguagePatternFormula,
  ManualModule,
  PracticeManualModule,
  PracticeTechnique,
  TechniqueCard,
  TechniqueVisualCue
} from "../types";
import { coldReadingCards } from "./coldReadingCards";

const createdAt = "2026-06-14T00:00:00.000Z";

const coldVisual: TechniqueVisualCue = {
  imageUrl: "/visuals/cold-reading-observe-loop.png",
  imageAlt: "상대의 발화를 관찰하고 확인 질문으로 바꾸는 콜드리딩 훈련 이미지",
  quickInstruction: "단정하지 말고 관찰한 단서를 확인 질문으로 전환한다.",
  generationPrompt: "ethical cold reading formula training, observation loop, Korean mobile app visual cue, no text"
};

const boundaryVisual: TechniqueVisualCue = {
  imageUrl: "/visuals/boundary-setting.png",
  imageAlt: "대화 경계를 차분하게 세우는 장면",
  quickInstruction: "상대의 프레임을 바로 받아들이지 않고 사실, 기준, 다음 행동으로 분리한다.",
  generationPrompt: "calm boundary setting communication practice, mobile app visual cue, no text"
};

const innerFocusVisual: TechniqueVisualCue = {
  imageUrl: "/visuals/inner-focus.png",
  imageAlt: "호흡과 중심 감각에 주의를 모으는 내면 집중 이미지",
  quickInstruction: "몸의 감각을 기준점으로 삼고 과장된 해석 없이 기록한다.",
  generationPrompt: "qigong inner focus practice, breath, palms, lower abdomen, calm mobile app visual cue, no text"
};

const intuitionVisual: TechniqueVisualCue = {
  imageUrl: "/visuals/intuition-choice.png",
  imageAlt: "직관 선택을 기록하고 결과를 비교하는 훈련 이미지",
  quickInstruction: "떠오른 선택을 먼저 기록하고, 결과 확인은 나중에 분리한다.",
  generationPrompt: "intuition choice journal training, three options, notebook, calm mobile app visual cue, no text"
};

function compactList(values: Array<string | undefined>): string[] {
  return Array.from(
    new Set(
      values
        .flatMap((value) => (value ?? "").split(/[,;\n]/))
        .map((value) => value.trim())
        .filter(Boolean)
    )
  );
}

function formulaFromCard(card: TechniqueCard, index: number): LanguagePatternFormula {
  return {
    id: "cold_formula_" + String(index + 1).padStart(2, "0"),
    group: card.formulaGroup || "콜드리딩 공식",
    name: card.title,
    manualFormula: card.manualFormula || card.formula || card.title,
    practiceFormula: card.practiceFormula || card.sentenceTemplates[0] || "단정 대신 확인 질문으로 바꾼다.",
    principle: card.principle,
    triggerSituations: compactList(card.triggerSituations),
    examples: compactList(card.exampleDialogues),
    cautions: compactList(card.warnings),
    tags: compactList(["콜드리딩", card.formulaGroup, ...card.tags]),
    visualCue: card.visualCue || coldVisual
  };
}

const coldReadingModule: LanguageManualModule = {
  id: "module_cold_reading_manual",
  domain: "language-pattern",
  title: "콜드리딩 공식 매뉴얼",
  description: "콜드리딩 매뉴얼의 공식을 하나의 언어 패턴 모듈로 묶어, 상황에 맞는 확인 질문과 반영 문장으로 전환한다.",
  category: "언어 패턴 / 콜드리딩",
  sourceLabel: "콜드리딩 메뉴얼.txt",
  tags: ["콜드리딩", "언어 패턴", "관찰", "확인 질문", "반영"],
  keywords: ["콜드리딩", "cold reading", "상대 파악", "성향", "관찰", "리딩", "확인"],
  active: true,
  sortOrder: 10,
  createdAt,
  updatedAt: createdAt,
  visualCue: coldVisual,
  formulas: coldReadingCards.map(formulaFromCard),
  revisions: []
};

const boundaryModule: LanguageManualModule = {
  id: "module_boundary_language_patterns",
  domain: "language-pattern",
  title: "경계 설정 언어 패턴",
  description: "압박, 비난, 과도한 요구를 사실 기준과 다음 행동으로 정리하는 문장 공식이다.",
  category: "언어 패턴 / 경계",
  sourceLabel: "기본 시드",
  tags: ["경계", "거절", "압박 대응", "명료화"],
  keywords: ["거절", "압박", "비난", "요구", "한계", "대화 종료"],
  active: true,
  sortOrder: 20,
  createdAt,
  updatedAt: createdAt,
  visualCue: boundaryVisual,
  formulas: [
    {
      id: "boundary_formula_fact_standard_action",
      group: "경계 설정",
      name: "사실-기준-다음 행동",
      manualFormula: "상대 표현을 사실로 축소한다 → 내 기준을 짧게 말한다 → 가능한 다음 행동을 제시한다.",
      practiceFormula: "방금 표현은 받아들이기 어렵습니다. 저는 구체적인 사실 기준으로만 이야기하겠습니다. 계속하려면 그 부분부터 정리해 주세요.",
      principle: "감정적 압박을 그대로 받지 않고 대화의 기준을 관찰 가능한 사실로 되돌린다.",
      triggerSituations: ["비난", "압박", "과도한 요구", "반복되는 말 끊기"],
      examples: ["상대: 네가 예민한 거야 / 나: 그 표현은 받아들이기 어렵습니다. 구체적인 상황을 기준으로 말해 주세요."],
      cautions: ["상대를 진단하거나 모욕하지 않는다.", "협박이나 보복 암시를 쓰지 않는다."],
      tags: ["경계", "사실 기준", "다음 행동"],
      visualCue: boundaryVisual
    },
    {
      id: "boundary_formula_delay_choice",
      group: "경계 설정",
      name: "즉답 보류-선택지 제시",
      manualFormula: "즉답을 거절한다 → 검토 시간을 확보한다 → 가능한 선택지를 제한해 제시한다.",
      practiceFormula: "지금 바로 결정하지 않겠습니다. 확인한 뒤에 오늘 안으로 답하겠습니다. 가능한 선택지는 두 가지로 정리해서 말씀드릴게요.",
      principle: "상대의 속도에 끌려가지 않고 판단 시간을 회복한다.",
      triggerSituations: ["급한 요구", "계약", "돈", "결정 압박"],
      examples: ["상대: 지금 정해 / 나: 지금 바로 결정하지 않겠습니다. 확인 후 답하겠습니다."],
      cautions: ["불필요한 변명을 길게 붙이지 않는다."],
      tags: ["보류", "선택지", "결정 압박"],
      visualCue: boundaryVisual
    },
    {
      id: "boundary_formula_exit_condition",
      group: "경계 설정",
      name: "종료 조건 명시",
      manualFormula: "대화 가능 조건을 말한다 → 조건이 지켜지지 않을 때의 종료 행동을 말한다.",
      practiceFormula: "이야기는 계속할 수 있습니다. 다만 비난 표현이 반복되면 저는 대화를 멈추고 나중에 다시 이야기하겠습니다.",
      principle: "상대 통제가 아니라 내 참여 조건을 분명히 한다.",
      triggerSituations: ["언성 높임", "모욕", "반복 압박", "무시"],
      examples: ["상대: 또 도망가네 / 나: 비난이 반복되면 대화가 어렵습니다. 나중에 다시 이야기하겠습니다."],
      cautions: ["상대에게 처벌을 예고하듯 말하지 않는다."],
      tags: ["종료", "조건", "거리두기"],
      visualCue: boundaryVisual
    }
  ],
  revisions: []
};

const qigongBasics: PracticeManualModule = {
  id: "module_qigong_sensation_basics",
  domain: "psi-qigong",
  title: "기공 기본 감각 훈련",
  description: "호흡, 손바닥, 단전 감각을 기준으로 주의 집중과 몸 감각 기록을 훈련한다.",
  category: "초능력/기공 / 감각",
  sourceLabel: "기본 시드",
  tags: ["기공", "기감", "호흡", "단전", "감각"],
  keywords: ["기공", "기감", "손바닥", "단전", "호흡", "에너지", "집중"],
  active: true,
  sortOrder: 100,
  createdAt,
  updatedAt: createdAt,
  visualCue: innerFocusVisual,
  practices: [
    {
      id: "practice_palm_sensation_scan",
      group: "기감 감각",
      name: "손바닥 기감 스캔",
      intent: "손바닥의 열감, 압력감, 미세한 진동 같은 주관적 감각을 안정적으로 관찰한다.",
      recommendedFor: ["기감 입문", "집중이 흩어질 때", "짧은 워밍업"],
      keywords: ["손바닥", "열감", "압력", "기감", "워밍업"],
      setup: ["앉거나 선 자세에서 어깨 힘을 뺀다.", "양손을 가슴 앞에서 15cm 정도 떨어뜨린다.", "시선은 부드럽게 아래로 둔다."],
      steps: ["세 번 천천히 숨을 내쉰다.", "양손 사이의 온도, 압력, 미세한 움직임을 관찰한다.", "손 사이 간격을 5cm 넓혔다 좁히며 감각 변화를 본다.", "감각을 해석하지 말고 단어 세 개로 기록한다."],
      duration: "5-8분",
      difficulty: "입문",
      safety: ["통증, 어지러움, 불안이 생기면 즉시 멈춘다.", "의학적 치료나 초능력 증명으로 사용하지 않는다."],
      customizationPrompts: ["오늘 가장 먼저 잡힌 감각은 무엇인가?", "간격을 바꿀 때 감각은 어떻게 달라졌는가?"],
      successCriteria: ["감각의 유무보다 기록의 일관성이 생긴다.", "과장된 확신 없이 관찰 단어를 남긴다."],
      cautions: ["결과를 억지로 만들려고 손에 힘을 주지 않는다."],
      tags: ["손바닥", "기감", "감각 기록"],
      visualCue: innerFocusVisual
    },
    {
      id: "practice_breath_dantian_centering",
      group: "중심화",
      name: "호흡-단전 중심화",
      intent: "아랫배 중심 감각을 기준점으로 삼아 감정과 주의력을 안정시킨다.",
      recommendedFor: ["긴장", "혼자 훈련", "대화 전 정렬"],
      keywords: ["단전", "호흡", "중심", "안정", "기공"],
      setup: ["등을 세우되 허리에 힘을 과하게 주지 않는다.", "한 손을 아랫배에 가볍게 올린다."],
      steps: ["들이마실 때 아랫배가 부드럽게 넓어진다고 느낀다.", "내쉴 때 어깨와 턱의 힘을 뺀다.", "생각이 흐르면 다시 손 아래 감각으로 돌아온다.", "끝나면 몸의 변화와 감정 변화를 한 줄로 적는다."],
      duration: "7-12분",
      difficulty: "입문",
      safety: ["호흡을 오래 참지 않는다.", "공황감이나 흉부 압박감이 있으면 멈춘다."],
      customizationPrompts: ["훈련 전후 긴장도는 몇 점인가?", "단전 감각은 무게, 온도, 압력 중 무엇에 가까운가?"],
      successCriteria: ["주의가 흩어져도 기준점으로 돌아오는 시간이 짧아진다."],
      cautions: ["특정 현상을 반드시 느껴야 한다고 몰아붙이지 않는다."],
      tags: ["단전", "호흡", "안정"],
      visualCue: innerFocusVisual
    }
  ],
  revisions: []
};

const intuitionTraining: PracticeManualModule = {
  id: "module_intuition_training",
  domain: "psi-qigong",
  title: "직관 선택 훈련",
  description: "예측을 단정하지 않고, 떠오른 선택과 실제 결과를 분리해 기록하는 직관 훈련이다.",
  category: "초능력/기공 / 직관",
  sourceLabel: "기본 시드",
  tags: ["직관", "기록", "선택", "이미지 집중"],
  keywords: ["직관", "예감", "선택", "이미지", "카드", "기록", "타깃"],
  active: true,
  sortOrder: 110,
  createdAt,
  updatedAt: createdAt,
  visualCue: intuitionVisual,
  practices: [
    {
      id: "practice_abc_choice_log",
      group: "직관 기록",
      name: "A/B/C 선택 기록",
      intent: "떠오른 선택을 먼저 기록하고 실제 결과와 나중에 비교한다.",
      recommendedFor: ["직관 훈련 입문", "패턴 기록", "결과 검증 습관"],
      keywords: ["A/B/C", "선택", "기록", "예감", "검증"],
      setup: ["결과를 아직 모르는 세 가지 선택지를 만든다.", "선택 전에 정답을 찾아보지 않는다."],
      steps: ["선택지를 A/B/C로 적는다.", "30초 동안 몸 감각과 떠오르는 이미지를 관찰한다.", "가장 먼저 끌리는 선택과 이유 한 줄을 기록한다.", "결과 확인 후 맞고 틀림보다 감각 패턴을 적는다."],
      duration: "3-6분",
      difficulty: "입문",
      safety: ["돈, 건강, 법률, 관계 파탄 같은 중대 결정에는 사용하지 않는다.", "기록은 훈련 자료이지 확정 예언이 아니다."],
      customizationPrompts: ["선택 직전 몸 감각은 어땠는가?", "맞은 날과 틀린 날의 공통점은 무엇인가?"],
      successCriteria: ["선택 전 기록과 결과 후 해석을 분리한다.", "연속 실패를 과도하게 의미화하지 않는다."],
      cautions: ["사후 끼워 맞추기를 피한다."],
      tags: ["직관", "기록", "검증"],
      visualCue: intuitionVisual
    },
    {
      id: "practice_target_image_focus",
      group: "이미지 집중",
      name: "목표 이미지 집중",
      intent: "무작위 대상에 대해 떠오르는 색, 형태, 질감 이미지를 짧게 기록한다.",
      recommendedFor: ["원격지각식 이미지 훈련", "상상력 정렬", "감각 묘사"],
      keywords: ["이미지", "타깃", "색", "형태", "질감", "원격지각"],
      setup: ["타깃은 봉투, 랜덤 이미지, 닫힌 파일명처럼 직접 보지 않는 대상으로 둔다.", "정답 확인 시간을 정해 둔다."],
      steps: ["타깃 이름이나 번호만 보고 60초간 떠오르는 감각을 적는다.", "명사보다 색, 모양, 질감, 움직임을 우선 기록한다.", "정답 확인 후 맞춘 부분과 빗나간 부분을 분리한다."],
      duration: "5-10분",
      difficulty: "중급",
      safety: ["결과를 초능력 증명으로 단정하지 않는다.", "불안하거나 집착이 생기면 빈도를 줄인다."],
      customizationPrompts: ["떠오른 정보는 감각인가, 추론인가?", "정답 확인 전 수정한 내용이 있는가?"],
      successCriteria: ["추론과 감각 기록을 구분한다.", "정답률보다 기록 품질을 본다."],
      cautions: ["사람의 사생활, 질병, 위험 예측에는 쓰지 않는다."],
      tags: ["이미지", "타깃", "기록"],
      visualCue: intuitionVisual
    }
  ],
  revisions: []
};

export const defaultManualModules: ManualModule[] = [
  coldReadingModule,
  boundaryModule,
  qigongBasics,
  intuitionTraining
];
